import {
  Document,
  DocumentSection,
  DocumentSectionContentElement,
  DocumentUpdateRequest,
} from '@/shared/api/transcriptor/transcriptorApi';

import {
  ConsultationNode,
  ConsultationSection,
  GroupedSection,
  SectionContent,
} from '../config';

// Removed BackendSummaryContent interface - not needed

// Mapping of English titles to translation keys
const TITLE_TRANSLATION_MAP: Record<string, string> = {
  // Node titles (supercategories)
  General: 'dentalConsultation.summary.general',
  'General Information': 'dentalConsultation.summary.generalInformation',
  Subjective: 'dentalConsultation.summary.subjective',
  Objective: 'dentalConsultation.summary.objective',
  Assessment: 'dentalConsultation.summary.assessment',
  Plan: 'dentalConsultation.summary.plan',

  // Section titles
  'Patient Name': 'dentalConsultation.summary.patientName',
  'Patient Date of Birth': 'dentalConsultation.summary.patientDateOfBirth',
  'Doctor Name': 'dentalConsultation.summary.doctorName',
  'Chief Complaint': 'dentalConsultation.summary.chiefComplaint',
  'History of Present Illness':
    'dentalConsultation.summary.historyPresentIllness',
  History: 'dentalConsultation.summary.history',
  'Review of Symptoms': 'dentalConsultation.summary.reviewSymptoms',
  Medications: 'dentalConsultation.summary.medications',
  'Clinical Examinations': 'dentalConsultation.summary.clinicalExaminations',
  'Imaging Results': 'dentalConsultation.summary.imagingResults',
  Diagnosis: 'dentalConsultation.summary.diagnosis',
  'Dental Hygiene Diagnosis':
    'dentalConsultation.summary.dentalHygieneDiagnosis',
  'Procedures Performed': 'dentalConsultation.summary.proceduresPerformed',
  'Medications Given': 'dentalConsultation.summary.medicationsGiven',
  'Education Provided': 'dentalConsultation.summary.educationProvided',
  'Referrals Given': 'dentalConsultation.summary.referralsGiven',
  'Follow-up Instructions': 'dentalConsultation.summary.followUpInstructions',

  // Additional common titles
  Contact: 'dentalConsultation.summary.contact',
  Interview: 'dentalConsultation.summary.interview',

  // Evaluation titles
  'Consultation Quality Assessment':
    'dentalConsultation.evaluation.consultationQualityAssessment',

  // Evaluation section titles (common categories in evaluation)
  'Documentation Quality': 'dentalConsultation.evaluation.documentationQuality',
  'Clinical Reasoning': 'dentalConsultation.evaluation.clinicalReasoning',
  'Professional Communication':
    'dentalConsultation.evaluation.professionalCommunication',
  'Overall Quality': 'dentalConsultation.evaluation.overallQuality',
  Completeness: 'dentalConsultation.evaluation.completeness',
  Accuracy: 'dentalConsultation.evaluation.accuracy',
  Relevance: 'dentalConsultation.evaluation.relevance',
  Clarity: 'dentalConsultation.evaluation.clarity',
  Organization: 'dentalConsultation.evaluation.organization',

  // Consultation subsection titles
  Presentation: 'dentalConsultation.evaluation.presentation',
  Justification: 'dentalConsultation.evaluation.justification',
  Negotiation: 'dentalConsultation.evaluation.negotiation',
  Closure: 'dentalConsultation.evaluation.closure',
  'Transcript Analysis': 'dentalConsultation.evaluation.transcriptAnalysis',

  // Follow-up titles
  'Follow-up Communication':
    'dentalConsultation.followUp.followUpCommunication',
  'Follow-up Letter': 'dentalConsultation.followUp.followUpLetter',
  'Follow-up letter': 'dentalConsultation.followUp.followUpLetter', // Backend sometimes sends with lowercase 'l'
  'Patient Communication': 'dentalConsultation.followUp.patientCommunication',
  'Literature Review': 'dentalConsultation.tab.literatureReview',
};

const groupSectionsIntoNodes = (
  items: GroupedSection[],
): ConsultationNode[] => {
  const grouped = items.reduce<Record<string, ConsultationSection[]>>(
    (acc, { supercategory, section }) => ({
      ...acc,
      [supercategory]: [...(acc[supercategory] ?? []), section],
    }),
    {},
  );

  return Object.entries(grouped).map(([supercategory, children]) => ({
    title: supercategory || undefined,
    titleKey: TITLE_TRANSLATION_MAP[supercategory],
    children,
  }));
};

const parseSectionContent = (section: DocumentSection): SectionContent[] => {
  if (section.user_content?.text) {
    return [{ text: section.user_content.text }];
  }

  return Array.isArray(section.content)
    ? section.content.map((item: DocumentSectionContentElement) => ({
        text: item.text || '',
        citations: item.citations,
      }))
    : [];
};

export const parseSummaryData = (
  summary: Document | null | undefined,
): ConsultationNode[] => {
  if (!summary?.sections?.length) {
    return [];
  }

  return groupSectionsIntoNodes(
    summary.sections.map((section) => ({
      supercategory: section.supercategory ?? '',
      section: {
        code: section.code,
        category: section.category,
        title: section.title,
        titleKey: TITLE_TRANSLATION_MAP[section.title],
        content: parseSectionContent(section),
        score: section.score,
      },
    })),
  );
};

export const parseEvaluationData = (
  evaluation: Document | null | undefined,
): ConsultationNode[] => {
  if (!evaluation?.sections?.length) {
    return [];
  }

  return groupSectionsIntoNodes(
    evaluation.sections.map((section) => {
      const textContent = section.user_content?.text
        ? section.user_content.text
        : Array.isArray(section.content)
          ? section.content
              .map((item: DocumentSectionContentElement) => item.text)
              .join(' ')
          : '';

      return {
        // The Evaluation tab keeps its default heading: unlike the other
        // documents, its ungrouped sections are still shown under one title.
        supercategory:
          section.supercategory || 'Consultation Quality Assessment',
        section: {
          code: section.code,
          category: section.category,
          title: section.title,
          titleKey: TITLE_TRANSLATION_MAP[section.title],
          content: [{ text: textContent }],
          score: section.score,
        },
      };
    }),
  );
};

export const parseFollowUpData = (
  followUp: Document | null | undefined,
): ConsultationNode[] => {
  if (!followUp?.sections?.length) {
    return [];
  }

  return groupSectionsIntoNodes(
    followUp.sections.map((section) => ({
      supercategory: section.supercategory ?? '',
      section: {
        code: section.code,
        category: section.category,
        title: section.title,
        titleKey: TITLE_TRANSLATION_MAP[section.title],
        content: parseSectionContent(section),
      },
    })),
  );
};

export const parseLiteratureReviewData = (
  literatureReview: Document | null | undefined,
): ConsultationNode[] => {
  if (!literatureReview?.sections?.length) {
    return [];
  }

  return groupSectionsIntoNodes(
    literatureReview.sections.map((section) => ({
      supercategory: section.supercategory ?? '',
      section: {
        code: section.code,
        category: section.category,
        title: section.title,
        titleKey: TITLE_TRANSLATION_MAP[section.title],
        content: parseSectionContent(section),
      },
    })),
  );
};

/**
 * Convert ConsultationNode data to DocumentUpdateRequest format for API.
 * Sections without a `code` are skipped — the PATCH contract identifies
 * sections by `code`, so they can't be updated even if we tried.
 */
export const convertNodesToDocumentUpdate = (
  nodes: ConsultationNode[],
): DocumentUpdateRequest => {
  const sections = nodes.flatMap((node) =>
    node.children.flatMap((section) =>
      section.code
        ? [
            {
              code: section.code,
              content: section.content.map((c) => c.text).join('\n'),
            },
          ]
        : [],
    ),
  );

  return { sections };
};
